import { useEffect, useRef } from "react";
import { Link } from "@tanstack/react-router";
import { Alert, Typography, Card } from "@/deps/ui-library";
import { api } from "@/api";
import { useHelpArticle } from "@/api/queries";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { EmptyState } from "@/components/EmptyState";
import { HelpGate } from "./HelpGate";
import { PlatformHtml } from "./PlatformHtml";

/**
 * One guide. When the platform keeps this reader out of it — an Administrator-only guide opened by
 * an Accountant — the API answers 403 with the gate, and its sentence is what the reader sees.
 */
export function HelpArticlePage({ slug }: { slug: string }) {
  const outcome = useHelpArticle(slug);
  const opened = outcome.data?.kind === "article" ? outcome.data.article.slug : null;
  const read = useRef<{ id: number | null; at: number }>({ id: null, at: 0 });

  // One read per opened guide, and how long it stayed open. Analytics: a failure is not shown.
  useEffect(() => {
    if (!opened) return;
    read.current = { id: null, at: Date.now() };
    void api.help
      .recordRead(opened)
      .then((result) => {
        read.current.id = result.readEventId;
      })
      .catch(() => undefined);
    return () => {
      const { id, at } = read.current;
      if (id !== null) {
        void api.help
          .recordReadDuration(id, Math.round((Date.now() - at) / 1000))
          .catch(() => undefined);
      }
    };
  }, [opened]);

  const data = outcome.data;

  return (
    <HelpGate section="guides">
      {data?.kind === "restricted" ? (
        <Alert
          role="alert"
          icon="Lock"
          config={{ variant: "subtle", colorScheme: "warning" }}
          title="This guide is not available to you"
          description={data.detail}
        >
          <Link to="/help" className="text-primary mt-1 inline-block text-xs">
            Back to the guides
          </Link>
        </Alert>
      ) : null}

      {data?.kind === "notFound" || outcome.isError ? (
        <EmptyState
          icon="BookX"
          title="That guide is not available"
          description="It may have been moved or unpublished. The guides you can open are listed on the Help page."
        />
      ) : null}

      {data?.kind === "article" ? (
        <>
          <Breadcrumbs
            items={[
              { label: "Guides", to: "/help" },
              { label: data.article.categoryName },
              { label: data.article.title },
            ]}
          />
          <Card>
            <article className="flex flex-col gap-2">
              <Typography.Text as="span" variant="small" muted>
                {data.article.categoryName} · {data.article.sectionName}
              </Typography.Text>
              <h2 className="text-foreground m-0 text-xl font-bold tracking-[-0.01em]">
                {data.article.title}
              </h2>
              {data.article.summary ? (
                <p className="text-muted-foreground m-0 text-sm">{data.article.summary}</p>
              ) : null}
              <PlatformHtml html={data.article.contentHtml} kind="article" />
              <nav
                aria-label="More guides"
                className="border-border mt-4 flex justify-between border-t pt-3 text-xs"
              >
                {data.article.previous ? (
                  <Link
                    to="/help/articles/$slug"
                    params={{ slug: data.article.previous.slug }}
                    className="text-primary"
                  >
                    ← {data.article.previous.title}
                  </Link>
                ) : (
                  <span />
                )}
                {data.article.next ? (
                  <Link
                    to="/help/articles/$slug"
                    params={{ slug: data.article.next.slug }}
                    className="text-primary"
                  >
                    {data.article.next.title} →
                  </Link>
                ) : null}
              </nav>
            </article>
          </Card>
        </>
      ) : null}
    </HelpGate>
  );
}
